/**
 * RouteMapModal - Carte IGN avec itinéraire et vue aérienne
 * Affiche l'itinéraire dépôt → destination avec fond ortho IGN
 */

import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API_BASE_URL } from '../utils/apiConfig';

// Fix pour les icônes Leaflet avec webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Icône personnalisée pour le dépôt (vert)
const depotIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Icône personnalisée pour la destination (rouge)
const destinationIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Composant pour ajuster la vue de la carte
function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length === 2) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, bounds]);
  return null;
}

// Composant pour forcer le redimensionnement de la carte (problème classique Leaflet dans les modales)
function InvalidateSize() {
  const map = useMap();
  useEffect(() => {
    // Invalider plusieurs fois pour s'assurer que la carte se rend correctement
    const timers = [100, 300, 500].map(delay =>
      setTimeout(() => {
        map.invalidateSize();
      }, delay)
    );
    return () => timers.forEach(t => clearTimeout(t));
  }, [map]);
  return null;
}

// Couches de tuiles disponibles
const TILE_LAYERS = {
  osm: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors',
    name: 'OpenStreetMap',
    subdomains: 'abc'
  },
  ortho: {
    url: 'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=ORTHOIMAGERY.ORTHOPHOTOS&STYLE=normal&FORMAT=image/jpeg&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}',
    attribution: '© IGN - BD ORTHO®',
    name: 'Vue aérienne'
  },
  plan: {
    url: 'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&STYLE=normal&FORMAT=image/png&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}',
    attribution: '© IGN - Plan IGN',
    name: 'Plan IGN'
  }
};

const RouteMapModal = ({
  isOpen,
  onClose,
  destination,        // { lat, lon, name, address, city }
  depot,              // { lat, lon, name } - optionnel, récupéré depuis API si non fourni
  cachedGeometry,     // Géométrie de l'itinéraire en cache (array de [lat, lon])
  cachedAerialPhoto,  // Photo aérienne en cache (base64)
  distanceKm,         // Distance en km (si déjà connue)
  token
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [routeGeometry, setRouteGeometry] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [depotCoords, setDepotCoords] = useState(depot || null);
  const [activeLayer, setActiveLayer] = useState('osm'); // OSM par défaut (plus fiable)
  const [aerialPhoto, setAerialPhoto] = useState(cachedAerialPhoto || null);
  const [showMap, setShowMap] = useState(false);
  const [viewMode, setViewMode] = useState('map'); // 'map' ou 'photo'
  const [loadingPhoto, setLoadingPhoto] = useState(false);

  // Récupérer les coordonnées du dépôt depuis l'API
  const fetchDepotSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/depot`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Coordonnées du dépôt non configurées');
      }

      const result = await response.json();
      const data = result.data || result;

      return {
        lat: parseFloat(data.latitude),
        lon: parseFloat(data.longitude),
        name: data.address || 'Dépôt'
      };
    } catch (err) {
      console.error('Erreur récupération dépôt:', err);
      throw err;
    }
  }, [token]);

  // Récupérer l'itinéraire depuis l'API IGN
  const fetchRoute = useCallback(async (fromLat, fromLon, toLat, toLon) => {
    try {
      const url = `https://data.geopf.fr/navigation/itineraire?resource=bdtopo-osrm&start=${fromLon},${fromLat}&end=${toLon},${toLat}&profile=car&optimization=fastest&getSteps=false&getBbox=true&distanceUnit=kilometer&timeUnit=minute&getGeometry=true`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Erreur calcul itinéraire');
      }

      const data = await response.json();

      // Extraire la géométrie de l'itinéraire
      let geometry = null;
      if (data.geometry && data.geometry.coordinates) {
        // Convertir de [lon, lat] à [lat, lon] pour Leaflet
        geometry = data.geometry.coordinates.map(coord => [coord[1], coord[0]]);
      }

      return {
        geometry,
        distance: data.distance ? Math.round(data.distance * 10) / 10 : null,
        duration: data.duration ? Math.round(data.duration) : null
      };
    } catch (err) {
      console.error('Erreur itinéraire IGN:', err);
      throw err;
    }
  }, []);

  // Récupérer la photo aérienne du point de RDV
  const fetchAerialPhoto = useCallback(async () => {
    if (!destination?.lat || !destination?.lon) return;

    setLoadingPhoto(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/geo/aerial-photo?lat=${destination.lat}&lon=${destination.lon}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error('Erreur récupération photo');
      }

      const data = await response.json();
      if (data.success && data.photo) {
        setAerialPhoto(data.photo);
      } else {
        throw new Error(data.error || 'Photo non disponible');
      }
    } catch (err) {
      console.error('Erreur photo aérienne:', err);
      setError('Impossible de charger la photo aérienne');
    } finally {
      setLoadingPhoto(false);
    }
  }, [destination, token]);

  // Charger les données au montage
  useEffect(() => {
    console.log('[RouteMapModal] Props reçues:', {
      isOpen,
      destination,
      cachedGeometry: cachedGeometry ? `${typeof cachedGeometry} (${Array.isArray(cachedGeometry) ? cachedGeometry.length + ' pts' : 'not array'})` : 'null',
      distanceKm
    });

    if (!isOpen || !destination?.lat || !destination?.lon) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Récupérer le dépôt si non fourni
        let depotData = depotCoords;
        if (!depotData) {
          depotData = await fetchDepotSettings();
          setDepotCoords(depotData);
        }

        // Utiliser la géométrie en cache si disponible
        let geometry = cachedGeometry;

        // Parser si c'est une chaîne JSON
        if (typeof geometry === 'string') {
          try {
            geometry = JSON.parse(geometry);
            console.log('[RouteMapModal] Géométrie parsée depuis JSON string');
          } catch (e) {
            console.error('[RouteMapModal] Erreur parsing géométrie:', e);
            geometry = null;
          }
        }

        if (geometry && Array.isArray(geometry) && geometry.length > 0) {
          console.log('[RouteMapModal] Utilisation du cache itinéraire:', geometry.length, 'points');
          setRouteGeometry(geometry);
          setRouteInfo({
            distance: distanceKm || null,
            duration: null // Pas en cache pour l'instant
          });
        } else {
          // Sinon, récupérer l'itinéraire via API IGN
          console.log('[RouteMapModal] Appel API IGN pour itinéraire');
          const route = await fetchRoute(
            depotData.lat,
            depotData.lon,
            destination.lat,
            destination.lon
          );

          console.log('[RouteMapModal] Réponse API IGN:', {
            hasGeometry: !!route.geometry,
            geometryLength: route.geometry?.length,
            distance: route.distance,
            duration: route.duration
          });

          setRouteGeometry(route.geometry);
          setRouteInfo({
            distance: route.distance,
            duration: route.duration
          });
        }

        // Photo aérienne (déjà initialisée depuis les props)
        if (cachedAerialPhoto) {
          console.log('[RouteMapModal] Photo aérienne en cache disponible');
        }

        console.log('[RouteMapModal] Chargement terminé - depot:', depotData, 'destination:', destination);
      } catch (err) {
        console.error('[RouteMapModal] Erreur:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, destination, depotCoords, fetchDepotSettings, fetchRoute, cachedGeometry, cachedAerialPhoto, distanceKm]);

  // Délai pour attendre que la modale soit visible avant de rendre la carte
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setShowMap(true);
        console.log('[RouteMapModal] showMap = true');
      }, 200);
      return () => clearTimeout(timer);
    } else {
      setShowMap(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Calculer les bornes de la carte
  const bounds = depotCoords && destination ? [
    [depotCoords.lat, depotCoords.lon],
    [destination.lat, destination.lon]
  ] : null;

  // Centre par défaut (France)
  const defaultCenter = [46.603354, 1.888334];
  const center = destination ? [destination.lat, destination.lon] : defaultCenter;

  console.log('[RouteMapModal] Render state:', {
    loading,
    error,
    hasDepotCoords: !!depotCoords,
    hasRouteGeometry: !!routeGeometry,
    routeGeometryLength: routeGeometry?.length,
    bounds,
    center
  });

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Itinéraire</h2>
            {destination && (
              <p style={styles.subtitle}>
                {destination.name || destination.address || destination.city}
              </p>
            )}
          </div>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>

        {/* Infos itinéraire */}
        {routeInfo && (
          <div style={styles.routeInfo}>
            <span style={styles.routeInfoItem}>
              <strong>{routeInfo.distance}</strong> km
            </span>
            <span style={styles.routeInfoSeparator}>•</span>
            <span style={styles.routeInfoItem}>
              <strong>{routeInfo.duration}</strong> min
            </span>
          </div>
        )}

        {/* Onglets Carte / Photo */}
        <div style={styles.viewModeSelector}>
          <button
            style={{
              ...styles.viewModeButton,
              ...(viewMode === 'map' ? styles.viewModeButtonActive : {})
            }}
            onClick={() => setViewMode('map')}
          >
            Carte & Itinéraire
          </button>
          <button
            style={{
              ...styles.viewModeButton,
              ...(viewMode === 'photo' ? styles.viewModeButtonActive : {})
            }}
            onClick={() => {
              setViewMode('photo');
              if (!aerialPhoto && !loadingPhoto) {
                fetchAerialPhoto();
              }
            }}
          >
            Photo du RDV
          </button>
        </div>

        {/* Sélecteur de couche (uniquement en mode carte) */}
        {viewMode === 'map' && (
          <div style={styles.layerSelector}>
            {Object.entries(TILE_LAYERS).map(([key, layer]) => (
              <button
                key={key}
                style={{
                  ...styles.layerButton,
                  ...(activeLayer === key ? styles.layerButtonActive : {})
                }}
                onClick={() => setActiveLayer(key)}
              >
                {layer.name}
              </button>
            ))}
          </div>
        )}

        {/* Contenu principal */}
        <div style={styles.mapContainer} id="route-map-container">
          {viewMode === 'photo' ? (
            /* === MODE PHOTO === */
            loadingPhoto ? (
              <div style={styles.loadingOverlay}>
                <div style={styles.spinner}></div>
                <p>Chargement de la photo aérienne...</p>
              </div>
            ) : aerialPhoto ? (
              <div style={styles.photoContainer}>
                <img
                  src={aerialPhoto}
                  alt="Vue aérienne du point de RDV"
                  style={styles.aerialImage}
                />
                <div style={styles.photoCaption}>
                  Vue aérienne IGN - {destination?.name || destination?.city || 'Point de RDV'}
                </div>
              </div>
            ) : (
              <div style={styles.noPhotoContainer}>
                <p style={styles.noPhotoText}>Photo aérienne non disponible</p>
                <button style={styles.retryButton} onClick={fetchAerialPhoto}>
                  Charger la photo
                </button>
              </div>
            )
          ) : error ? (
            /* === MODE CARTE - ERREUR === */
            <div style={styles.errorOverlay}>
              <p style={styles.errorText}>{error}</p>
              <button style={styles.retryButton} onClick={() => window.location.reload()}>
                Réessayer
              </button>
            </div>
          ) : !showMap ? (
            /* === MODE CARTE - CHARGEMENT === */
            <div style={styles.loadingOverlay}>
              <div style={styles.spinner}></div>
              <p>Préparation de la carte...</p>
            </div>
          ) : (
            /* === MODE CARTE - AFFICHAGE === */
            <>
              <MapContainer
                key={`map-${activeLayer}`}
                center={center}
                zoom={13}
                style={{ height: '450px', width: '100%' }}
                scrollWheelZoom={true}
                whenReady={(map) => {
                  console.log('[RouteMapModal] Map ready, invalidating size');
                  setTimeout(() => map.target.invalidateSize(), 100);
                }}
              >
                <TileLayer
                  key={activeLayer}
                  url={TILE_LAYERS[activeLayer].url}
                  attribution={TILE_LAYERS[activeLayer].attribution}
                  maxZoom={19}
                  subdomains={TILE_LAYERS[activeLayer].subdomains || 'abc'}
                  eventHandlers={{
                    tileload: () => console.log('[RouteMapModal] Tile loaded'),
                    tileerror: (e) => console.error('[RouteMapModal] Tile error:', e.tile?.src)
                  }}
                />

                {/* Forcer le redimensionnement et ajuster la vue aux bornes */}
                <InvalidateSize />
                {bounds && <FitBounds bounds={bounds} />}

                {/* Marqueur dépôt */}
                {depotCoords && (
                  <Marker position={[depotCoords.lat, depotCoords.lon]} icon={depotIcon}>
                    <Popup>
                      <strong>Dépôt</strong>
                      <br />
                      {depotCoords.name}
                    </Popup>
                  </Marker>
                )}

                {/* Marqueur destination */}
                {destination && (
                  <Marker position={[destination.lat, destination.lon]} icon={destinationIcon}>
                    <Popup>
                      <strong>{destination.name || 'Destination'}</strong>
                      {destination.address && <><br />{destination.address}</>}
                      {destination.city && <><br />{destination.city}</>}
                    </Popup>
                  </Marker>
                )}

                {/* Tracé de l'itinéraire */}
                {routeGeometry && (
                  <Polyline
                    positions={routeGeometry}
                    color="#2196F3"
                    weight={5}
                    opacity={0.8}
                  />
                )}
              </MapContainer>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <a
            href={`https://www.google.com/maps/dir/${depotCoords?.lat},${depotCoords?.lon}/${destination?.lat},${destination?.lon}`}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.externalLink}
          >
            Ouvrir dans Google Maps
          </a>
          <button style={styles.closeButtonFooter} onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px'
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '16px 20px',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#f8f9fa'
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#333'
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: '#666'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#666',
    padding: '0 8px',
    lineHeight: '1'
  },
  routeInfo: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#e3f2fd',
    gap: '12px'
  },
  routeInfoItem: {
    fontSize: '16px',
    color: '#1565c0'
  },
  routeInfoSeparator: {
    color: '#90caf9'
  },
  layerSelector: {
    display: 'flex',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e0e0e0'
  },
  layerButton: {
    padding: '6px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'all 0.2s'
  },
  layerButtonActive: {
    backgroundColor: '#1976d2',
    color: 'white',
    borderColor: '#1976d2'
  },
  viewModeSelector: {
    display: 'flex',
    gap: '0',
    padding: '0',
    backgroundColor: '#e9ecef',
    borderBottom: '1px solid #dee2e6'
  },
  viewModeButton: {
    flex: 1,
    padding: '12px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#666',
    transition: 'all 0.2s',
    borderBottom: '3px solid transparent'
  },
  viewModeButtonActive: {
    backgroundColor: 'white',
    color: '#1976d2',
    borderBottom: '3px solid #1976d2'
  },
  photoContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5'
  },
  aerialImage: {
    maxWidth: '100%',
    maxHeight: '420px',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    border: '2px solid #e0e0e0'
  },
  photoCaption: {
    marginTop: '12px',
    fontSize: '13px',
    color: '#666',
    textAlign: 'center'
  },
  noPhotoContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa'
  },
  noPhotoText: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '16px'
  },
  mapContainer: {
    width: '100%',
    height: '450px',
    minHeight: '400px',
    position: 'relative',
    overflow: 'hidden'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e0e0e0',
    borderTopColor: '#1976d2',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  errorOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },
  errorText: {
    color: '#d32f2f',
    marginBottom: '16px',
    textAlign: 'center'
  },
  retryButton: {
    padding: '8px 16px',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#f8f9fa'
  },
  externalLink: {
    color: '#1976d2',
    textDecoration: 'none',
    fontSize: '14px'
  },
  closeButtonFooter: {
    padding: '8px 20px',
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  }
};

// Charger le CSS Leaflet via CDN comme fallback
if (!document.getElementById('leaflet-css-cdn')) {
  const leafletCss = document.createElement('link');
  leafletCss.id = 'leaflet-css-cdn';
  leafletCss.rel = 'stylesheet';
  leafletCss.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  leafletCss.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
  leafletCss.crossOrigin = '';
  document.head.appendChild(leafletCss);
}

// Ajouter les styles CSS nécessaires
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Fix Leaflet dans les modales */
  #route-map-container .leaflet-container {
    height: 450px !important;
    width: 100% !important;
    background: #e5e3df;
  }
`;
document.head.appendChild(styleSheet);

export default RouteMapModal;
